import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  ReferenceLine,
} from "recharts";

import loader from "../../../assets/images/datahub-loader.svg";
import { supplierColors } from "../const/supplierColorMapping";

const chartTitleStyle: React.CSSProperties = {
  color: "#263238",
  fontFamily: "var(--font-subheading, 'Palanquin Dark', sans-serif)",
  fontSize: "14px",
  fontWeight: 700,
  lineHeight: 1.1,
  margin: "0 0 18px",
  textAlign: "center",
};

const ChartTitle = ({ title }: { title: string }) => (
  <div style={chartTitleStyle}>{title}</div>
);

/* ---------------- CUSTOM LEGEND ---------------- */
const CustomLegend = ({ payload = [], series = [], hiddenSeries, toggleSeries, hoveredSeries, onMouseEnter, onMouseLeave, supplierColors }) => {
  // Build legend from series prop instead of payload to ensure all items are always visible
  // even when lines are hidden
  if (!series || series.length === 0) return null;

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        justifyContent: "flex-end",
        paddingRight: 10,
        paddingBottom: 5,
        cursor: "pointer",
        maxHeight: "150px",
        overflowY: "auto",
        overflowX: "hidden",
        lineHeight: "1.2",
      }}
    >
      {series.map((s) => {
        const isHidden = hiddenSeries.has(s.name);
        const color = supplierColors?.[s.supplierId?.toUpperCase()] ?? "#000";

        return (
          <div
            key={s.name}
            onClick={() => toggleSeries(s.name)}
            onMouseEnter={() => onMouseEnter(s.name)}
            onMouseLeave={onMouseLeave}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                opacity: 1, // Always visible, never hide legend
                filter: isHidden ? "blur(0.5px)" : "none",
                marginBottom: "2px",
              }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: color,
                display: "inline-block",
                flexShrink: 0,
                filter: isHidden ? "blur(0.5px)" : "none",
              }}
            />
            <span style={{ fontSize: 11, whiteSpace: "nowrap" }}>{s.name}</span>
          </div>
        );
      })}
    </div>
  );
};

/* ---------------- CUSTOM TOOLTIP ---------------- */
const CustomTooltip = ({ active, payload, label, coordinate, onActiveIndexChange, data }: any) => {
  // Update active index when tooltip becomes active
  useEffect(() => {
    if (active && label && data && onActiveIndexChange) {
      // Find the index of the current label in the data
      const index = data.findIndex((d: any) => d.date === label);
      if (index !== -1) {
        onActiveIndexChange(index);
      }
    } else if (!active && onActiveIndexChange) {
      onActiveIndexChange(null);
    }
  }, [active, label, data, onActiveIndexChange]);

  if (!active || !payload || !payload.length) return null;

  // Filter out duplicate entries (solid and dashed lines with same dataKey)
  // Dashed lines have empty name, so we filter them out
  const uniquePayload = [];
  const seen = new Set();
  
  payload.forEach((entry) => {
    // Skip entries with empty name (dashed lines) and duplicates
    if (entry.name && !seen.has(entry.dataKey)) {
      seen.add(entry.dataKey);
      uniquePayload.push(entry);
    }
  });

  return (
    <div
      style={{
        padding: "12px 14px",
        minWidth: "190px",
        borderRadius: "10px",
        background: "#fff",
        boxShadow: "0 10px 25px rgba(0,0,0,0.18)",
        border: "1px solid rgba(0,0,0,0.04)",
      }}
    >
      <div
        style={{
          fontSize: "12px",
          fontWeight: 700,
          color: "#2a2a2a",
          marginBottom: "8px",
        }}
      >
        {label}
      </div>
      {uniquePayload
        .filter((entry) => entry.value !== null && entry.value !== undefined)
        .sort((a, b) => (b.value || 0) - (a.value || 0))
        .map((entry, idx) => (
          <div
            key={idx}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
              padding: "4px 0",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  background: entry.color,
                }}
              />
              <span style={{ fontSize: "12px", color: "#4a4a4a" }}>
                {entry.name}
              </span>
            </div>
            <span style={{ fontWeight: 600, fontSize: "12px" }}>
              {typeof entry.value === "number"
                ? entry.value.toFixed(5)
                : "N/A"}
            </span>
          </div>
        ))}
    </div>
  );
};

/* ---------------- MAIN COMPONENT ---------------- */
const PriceTrendChartRechart = ({
  title = "Pricing Chart",
  series = [],
  categories = [],
  rawDates = [],
  isLoading,
  onPointClick,
}: {
  title?: string;
  series?: any[];
  categories?: string[];
  rawDates?: string[];
  isLoading?: boolean | null;
  onPointClick?: (bookedDate: string, supplierId: string) => void;
}) => {
  /* ---------------- HIDE / SHOW STATE ---------------- */
  const [hiddenSeries, setHiddenSeries] = useState(new Set<string>());
  const [hoveredSeries, setHoveredSeries] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  const toggleSeries = (name: string) => {
    setHiddenSeries((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name); // Unhide
      } else {
        next.add(name); // Hide
      }
      return next;
    });
  };

  const handleLegendMouseEnter = (name: string) => {
    setHoveredSeries(name);
  };

  const handleLegendMouseLeave = () => {
    setHoveredSeries(null);
  };

  /* ---------------- LOADING ---------------- */
  // Show loader if isLoading is true, undefined, or null (initial state) - same as PriceTrendChart
  if (isLoading != false) {
    return (
      <>
        <ChartTitle title={title} />
        <div className="d-flex justify-content-center align-items-center mt-5 mb-5" style={{ height: '400px' }}>
          <img src={loader} height={'100px'} alt="Loading" />
        </div>
        <hr/>
      </>
    );
  }

  /* ---------------- NO DATA ---------------- */
  // Only show "No data available" when explicitly not loading AND we have empty arrays (data was fetched but empty)
  if (isLoading === false && (!series || series.length === 0 || !categories || categories.length === 0)) {
    return (
      <>
        <ChartTitle title={title} />
        <div className="d-flex justify-content-center align-items-center mt-5 mb-5" style={{ height: '400px' }}>
          No data available for the selected filters!
        </div>
        <hr/>
      </>
    );
  }

  /* ---------------- DATA TRANSFORM ---------------- */
  const data = categories.map((label, index) => {
    const row: any = { date: label, rawDate: rawDates?.[index] ?? null };

    series.forEach((s) => {
      row[s.name] = s.data?.[index] ?? null;
    });

    return row;
  });

  /* ---------------- CALCULATE Y-AXIS DOMAIN ---------------- */
  // Calculate min and max values from all visible series to set proper domain
  const calculateYAxisDomain = () => {
    const visibleSeries = series.filter((s) => !hiddenSeries.has(s.name));
    if (visibleSeries.length === 0) return ['auto', 'auto'];

    const allValues: number[] = [];
    visibleSeries.forEach((s) => {
      if (s.data) {
        s.data.forEach((val) => {
          if (val !== null && val !== undefined && typeof val === 'number') {
            allValues.push(val);
          }
        });
      }
    });

    if (allValues.length === 0) return ['auto', 'auto'];

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue;
    
    // Add padding (10% of range) to top and bottom for better visualization
    const padding = range * 0.1;
    const domainMin = Math.max(0, minValue - padding); // Don't go below 0 for prices
    const domainMax = maxValue + padding;

    return [domainMin, domainMax];
  };

  const yAxisDomain = calculateYAxisDomain();

  const handleDotClick = (dotProps: any, supplierId: string | undefined) => {
    const payload = dotProps?.payload;
    if (!onPointClick || !supplierId) return;
    const rawDate = payload?.rawDate || payload?.date;
    if (!rawDate) return;

    // rawDate comes as "MM/DD/YY" (e.g. "01/06/26"), convert to "YYYY-MM-DD"
    let bookedDate = rawDate;
    const parts = String(rawDate).split("/");
    if (parts.length === 3) {
      const [mm, dd, yy] = parts;
      const yearNum = Number(yy);
      if (!isNaN(yearNum)) {
        const fullYear = yearNum >= 50 ? 1900 + yearNum : 2000 + yearNum;
        const month = mm.padStart(2, "0");
        const day = dd.padStart(2, "0");
        bookedDate = `${fullYear}-${month}-${day}`;
      }
    }

    onPointClick(bookedDate, supplierId);
  };

  /* ---------------- CHART ---------------- */
  return (
    <>
      <ChartTitle title={title} />

      {/* Legend - Outside chart container */}
      <div style={{ marginBottom: '10px', width: '100%' }}>
        <CustomLegend
          series={series}
          hiddenSeries={hiddenSeries}
          toggleSeries={toggleSeries}
          hoveredSeries={hoveredSeries}
          onMouseEnter={handleLegendMouseEnter}
          onMouseLeave={handleLegendMouseLeave}
          supplierColors={supplierColors}
        />
      </div>

      <ResponsiveContainer width="100%" height={500}>
        <LineChart
          data={data}
          margin={{ top: 10, right: 30, left: 20, bottom: 20 }}
        >
          {/* ❌ No CartesianGrid = no background lines */}

          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 12 }}
            interval="preserveStartEnd"
          />
          <YAxis 
            domain={yAxisDomain}
            tick={{ fontSize: 12 }}
            width={60}
            axisLine={true}
            label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
            tickFormatter={(value) => typeof value === 'number' ? value.toFixed(4) : value}
          />
          <Tooltip 
            content={(props) => (
              <CustomTooltip 
                {...props} 
                data={data}
                onActiveIndexChange={setActiveIndex}
              />
            )}
          />

          {/* Dashed reference line on hover */}
          {activeIndex !== null && data[activeIndex] && (
            <ReferenceLine
              x={data[activeIndex].date}
              stroke="#999"
              strokeDasharray="6 4"
              strokeWidth={1}
              style={{ filter: "blur(1px)" }}
            />
          )}

          {/* Lines */}
          {series.map((s) => {
            const color =
              supplierColors[s.supplierId?.toUpperCase()] ?? "#000";

            const isHidden = hiddenSeries.has(s.name);
            // Blur logic: blur if hovered (and not this series)
            const isBlurred = hoveredSeries !== null && hoveredSeries !== s.name;

            // Hide the line if it's in hiddenSeries
            if (isHidden) return null;

            return (
              <Line
                key={s.name}
                type="monotone"
                dataKey={s.name}
                stroke={color}
                strokeWidth={3}
                activeDot={(dotProps: any) => {
                  const value = dotProps?.payload?.[s.name];
                  if (value === null || value === undefined) {
                    return null;
                  }
                  if (isBlurred) return null;
                  const handleClick = () => handleDotClick(dotProps, s.supplierId);
                  return (
                    <g>
                      <circle
                        cx={dotProps.cx}
                        cy={dotProps.cy}
                        r={6}
                        fill="white"
                        stroke={color}
                        strokeWidth={1}
                        style={{ cursor: "pointer" }}
                        onClick={handleClick}
                      />
                      <circle
                        cx={dotProps.cx}
                        cy={dotProps.cy}
                        r={4}
                        fill={color}
                        style={{ cursor: "pointer" }}
                        onClick={handleClick}
                      />
                    </g>
                  );
                }}
                connectNulls={true}
                strokeOpacity={isBlurred ? 0.2 : 1}
                style={{ filter: isBlurred ? "blur(2px)" : "none" }}
                isAnimationActive={false}
                animationDuration={0}
              />
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      <hr />
    </>
  );
};

export default PriceTrendChartRechart;



